import {
  BarChartIcon,
  Button,
  ClockIcon,
  IconButton,
  RefreshCwIcon,
  Text,
  ScrollArea,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@buffer-mono/popcorn'
import { clsx } from 'clsx'
import { formatDistanceToNowStrict } from 'date-fns'
import * as React from 'react'
import {
  usePostData,
  useTrackActionClicked,
} from '~publish/components/PostCard/PostCardContext'
import { SplitProtected } from '~publish/components/SplitProtected'
import type { PostMetric as Metric } from '~publish/gql/graphql'
import { requiresRefreshing } from '~publish/components/PostCard/helpers'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import { useHasPrivilege } from '~publish/pages/Settings/hooks/useHasPrivilege'
import { useHorizontalScroll } from '~publish/hooks/useHorizontalScroll'
import styles from './PostMetrics.module.css'
import { PostMetric } from './PostMetric'

export const PostMetricsWrapper = ({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}): JSX.Element | null => {
  const { schedulingType, status, metrics } = usePostData()

  // Notifications don't have metrics
  if (schedulingType === 'notification' || status !== 'sent' || !metrics) {
    return null
  }

  return (
    <section
      aria-label="Analytics"
      className={clsx(styles.metricsWrapper, className)}
    >
      {children}
    </section>
  )
}

type MoreAnalyticsButtonProps = {
  analyzeUrl: string
}

export const MoreAnalyticsButton = ({
  analyzeUrl,
}: MoreAnalyticsButtonProps): JSX.Element => {
  const trackPostAction = useTrackActionClicked()

  return (
    <Button
      variant="secondary"
      as="a"
      href={analyzeUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(): void => trackPostAction('seeMoreAnalytics-2')}
      className={styles.analyzeCtaCollapsible}
    >
      <BarChartIcon />
      More Analytics...
    </Button>
  )
}

export const MoreAnalyticsIconButton = ({
  analyzeUrl,
}: MoreAnalyticsButtonProps): JSX.Element => {
  const trackPostAction = useTrackActionClicked()

  return (
    <IconButton
      label="See More Analytics"
      tooltip="See more analytics in Analyze"
      size="medium"
      as="a"
      href={analyzeUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(): void => trackPostAction('seeMoreAnalytics-2')}
      className="analyzeCta"
    >
      <BarChartIcon />
    </IconButton>
  )
}

type ScrollButtonProps = {
  direction: 'left' | 'right'
  isVisible: boolean
  onClick: () => void
}

const ScrollButton = ({
  direction,
  isVisible,
  onClick,
}: ScrollButtonProps): JSX.Element | null => {
  if (!isVisible) return null

  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon
  const className =
    direction === 'left' ? styles.scrollButtonLeft : styles.scrollButtonRight

  return (
    <IconButton
      label={`Scroll ${direction}`}
      size="small"
      variant="secondary"
      onClick={onClick}
      className={className}
    >
      <Icon />
    </IconButton>
  )
}

export const PostMetricsScroller = ({
  metrics,
  engageUrl,
}: {
  metrics: Metric[]
  engageUrl: string | null
}): JSX.Element => {
  const [isHovered, setIsHovered] = React.useState(false)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight, scrollLeft, scrollRight } =
    useHorizontalScroll(scrollRef, {
      scrollAmount: 200,
    })

  return (
    <section
      aria-label="Scrollable post metrics"
      className={styles.metricsScrollerContainer}
      onMouseEnter={(): void => setIsHovered(true)}
      onMouseLeave={(): void => setIsHovered(false)}
    >
      <ScrollButton
        direction="left"
        isVisible={canScrollLeft && isHovered}
        onClick={scrollLeft}
      />

      <ScrollArea
        ref={scrollRef}
        role="group"
        aria-label="Metrics list"
        scrollbars="horizontal"
        visibility="none"
        className={styles.metricsScroller}
        gradientFade={50}
      >
        <div className={styles.metricsScrollerContent}>
          {metrics.map((metric) => (
            <PostMetric
              key={metric.name}
              type={metric.type}
              name={metric.displayName}
              value={metric.nullableValue}
              unit={metric.unit}
              engageUrl={engageUrl}
            />
          ))}
        </div>
      </ScrollArea>

      <ScrollButton
        direction="right"
        isVisible={canScrollRight && isHovered}
        onClick={scrollRight}
      />
    </section>
  )
}

export const MetricsRefresh = ({
  className,
}: {
  className?: string
}): JSX.Element | null => {
  const { metrics, metricsUpdatedAt, status } = usePostData()

  if (status !== 'sent' || !metrics) {
    // eslint-disable-next-line no-console
    console.log('debug: returning null for MetricsRefresh')
    return null
  }

  const hasNoData = metrics.every(
    (metric) => typeof metric.nullableValue !== 'number',
  )

  return (
    <SplitProtected name="CT-show-metric-refresh">
      {metricsUpdatedAt ? (
        <div className={clsx(styles.lastUpdated, className)}>
          <ClockIcon size="xsmall" color="subtle" />
          <Text size="sm" color="subtle">
            Refreshed{' '}
            {formatDistanceToNowStrict(metricsUpdatedAt, { addSuffix: true })}
          </Text>
        </div>
      ) : null}
      {!metricsUpdatedAt && hasNoData ? (
        <div className={clsx(styles.lastUpdated, className)}>
          <ClockIcon size="xsmall" color="subtle" />
          <Text size="sm" color="subtle">
            Metrics not available yet
          </Text>
        </div>
      ) : null}
    </SplitProtected>
  )
}

export const RefreshConnection = ({
  className,
}: {
  className?: string
}): JSX.Element | null => {
  const { channel, status } = usePostData()
  const refreshChannelConnection = useRefreshChannelConnection()
  const canManageChannels = useHasPrivilege('canManageChannels')

  if (status !== 'sent' || !requiresRefreshing(channel)) {
    return null
  }

  const handleRefreshChannel = (cta: string): void => {
    if (canManageChannels) {
      refreshChannelConnection({
        channel,
        cta,
      })
    }
  }

  return (
    <section className={clsx(styles.refreshConnectionWrapper, className)}>
      <Text>Refresh your LinkedIn connection to unlock more metrics</Text>
      <Button
        variant="secondary"
        size="medium"
        disabled={!canManageChannels}
        onClick={(): void =>
          handleRefreshChannel(
            'publish-sentPosts-postMetrics-refreshConnection-1',
          )
        }
      >
        <RefreshCwIcon />
        Refresh Connection
      </Button>
    </section>
  )
}
